import { css } from 'lit';

/**
 * Mirrors `analyze-result-block/styles.ts` so the two cards read as
 * a designed pair. Only the kind-badge colour differs (purple for DR
 * vs green for analyze, matching the Deep Research mode tag colour).
 */
export const deepResearchResultStyles = css`
  :host {
    display: block;
    /* Larger top margin so the card breathes from the research-log
       block above. Bottom margin gives the markdown body room. */
    margin: 24px 0 16px;
    font-family: var(--affine-font-sans-family, system-ui, sans-serif);
  }

  .card {
    border: 1px solid var(--affine-border-color);
    border-radius: 10px;
    background: var(--affine-background-primary-color);
    color: var(--affine-text-primary-color);
    padding: 16px 18px;
    font-size: 13px;
    line-height: 1.5;
  }

  /* Top row: title + badge always together on one line, actions
     wrap to a second row when the badge is long. We use a 2-row
     grid so the toolbar never shoves the title around. */
  .header {
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: 16px;
    row-gap: 8px;
    align-items: center;
    padding-bottom: 12px;
    border-bottom: 1px dashed var(--affine-border-color);
    margin-bottom: 12px;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0; /* allow ellipsis on the badge inside */
    flex-wrap: nowrap;
  }

  .title {
    font-weight: 600;
    font-size: 13px;
    color: var(--affine-text-primary-color);
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* Kind-badge — kept short. The renderer caps the visible string;
     full theme is in the title attribute on hover. */
  .kind-badge {
    background: rgba(139, 92, 246, 0.12);
    color: #7c3aed;
    border-radius: 999px;
    padding: 2px 10px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 0 1 auto;
  }

  .spacer {
    flex: 1;
  }

  .actions {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-end;
    /* When the title row wraps (long badge), actions slot below */
    grid-column: 1 / -1;
  }

  /* Side-by-side when there's room (≥600px) */
  @media (min-width: 600px) {
    .actions {
      grid-column: auto;
      justify-content: flex-end;
    }
  }

  .btn {
    appearance: none;
    background: transparent;
    border: 1px solid var(--affine-border-color);
    color: var(--affine-text-secondary-color);
    padding: 3px 10px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 11px;
    font-family: inherit;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: color 80ms ease, border-color 80ms ease;
  }

  .btn:hover {
    color: var(--affine-primary-color, #1e88e5);
    border-color: var(--affine-primary-color, #1e88e5);
  }

  .btn[data-disabled='true'] {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .btn[data-disabled='true']:hover {
    color: var(--affine-text-secondary-color);
    border-color: var(--affine-border-color);
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 10px;
    margin: 0 0 4px;
    padding: 0;
  }

  .meta-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    background: var(--affine-hover-color, rgba(0, 0, 0, 0.03));
    border-radius: 6px;
    font-size: 12px;
    color: var(--affine-text-primary-color);
    line-height: 1.4;
  }

  .meta-item .label {
    color: var(--affine-text-secondary-color);
    font-weight: 500;
  }

  .meta-item .value {
    color: var(--affine-text-primary-color);
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: 11.5px;
  }

  .toast {
    position: fixed;
    bottom: 24px;
    right: 24px;
    background: var(--affine-text-primary-color);
    color: var(--affine-background-primary-color);
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 12px;
    z-index: 9999;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
  }

  .theme-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
  }

  /* Section label above a chip-row — small uppercase, secondary
     colour so the chips are the visual focus. */
  .section-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--affine-text-secondary-color);
    margin: 14px 0 6px;
  }

  .theme-chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    background: rgba(139, 92, 246, 0.08);
    color: #7c3aed;
    font-size: 11.5px;
    font-weight: 500;
    /* Cap chip width so a long sentence-style theme doesn't
       balloon the row; full text in the title attribute. */
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    /* When the chip is rendered as a <button> (Gap-5 suggestion
       chips), strip the default button chrome so it visually
       matches the read-only theme spans. */
    border: none;
    font-family: inherit;
    cursor: pointer;
    transition: background 80ms ease, transform 80ms ease;
  }

  button.theme-chip:hover {
    background: rgba(139, 92, 246, 0.16);
    transform: translateY(-1px);
  }

  /* Bundle search (Gap-4) */
  .bundle-search {
    margin-top: 14px;
  }

  .bundle-search-input {
    width: 100%;
    box-sizing: border-box;
    padding: 6px 10px;
    border: 1px solid var(--affine-border-color);
    border-radius: 6px;
    background: var(--affine-background-primary-color);
    color: var(--affine-text-primary-color);
    font-size: 12px;
    font-family: inherit;
    outline: none;
    transition: border-color 80ms ease;
  }

  .bundle-search-input:focus {
    border-color: var(--affine-primary-color, #1e88e5);
  }

  .bundle-search-input::placeholder {
    color: var(--affine-text-secondary-color);
  }

  .bundle-search-results {
    list-style: none;
    margin: 8px 0 0;
    padding: 0;
    border: 1px solid var(--affine-border-color);
    border-radius: 6px;
    overflow: hidden;
    max-height: 240px;
    overflow-y: auto;
  }

  .bundle-search-results li {
    padding: 8px 10px;
    border-bottom: 1px solid var(--affine-border-color);
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 11.5px;
    line-height: 1.4;
  }

  .bundle-search-results li:last-child {
    border-bottom: none;
  }

  .bundle-search-doc {
    font-weight: 600;
    color: var(--affine-text-secondary-color);
    font-size: 10.5px;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .bundle-search-snippet {
    color: var(--affine-text-primary-color);
  }

  .bundle-search-snippet mark {
    background: rgba(139, 92, 246, 0.22);
    color: inherit;
    padding: 0 2px;
    border-radius: 2px;
  }

  .bundle-search-empty {
    margin-top: 6px;
    font-size: 11.5px;
    color: var(--affine-text-secondary-color);
  }

  /* Per-document tabbed viewer — one tab per bundle doc, capped at
     10. Lets researchers validate the synthesis against any single
     source without leaving the result card. */
  .doc-tabs {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
    border: 1px solid var(--affine-border-color);
    border-radius: 8px;
    overflow: hidden;
    background: var(--affine-background-secondary-color);
  }

  .doc-tabs-strip {
    display: flex;
    gap: 2px;
    overflow-x: auto;
    background: var(--affine-background-primary-color);
    border-bottom: 1px solid var(--affine-border-color);
    padding: 4px 4px 0;
  }

  .doc-tab {
    appearance: none;
    background: transparent;
    border: 1px solid transparent;
    border-bottom: none;
    border-radius: 6px 6px 0 0;
    padding: 6px 10px;
    font-size: 11.5px;
    color: var(--affine-text-secondary-color);
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .doc-tab[aria-selected='true'] {
    color: var(--affine-text-primary-color);
    background: var(--affine-background-secondary-color);
    border-color: var(--affine-border-color);
    font-weight: 600;
  }

  .doc-tab:hover {
    color: var(--affine-text-primary-color);
  }

  .doc-tab-body {
    padding: 10px 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .doc-tab-summary {
    font-size: 12px;
    line-height: 1.5;
    color: var(--affine-text-primary-color);
    white-space: pre-wrap;
  }

  .doc-tab-section-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--affine-text-secondary-color);
    font-weight: 600;
  }

  .doc-tab-findings {
    margin: 0;
    padding-left: 18px;
    font-size: 11.5px;
    color: var(--affine-text-primary-color);
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .doc-tab-chunks {
    border: 1px solid var(--affine-border-color);
    border-radius: 6px;
    padding: 8px 10px;
    background: var(--affine-background-primary-color);
    font-size: 11.5px;
    line-height: 1.55;
    color: var(--affine-text-primary-color);
    max-height: 220px;
    overflow-y: auto;
    white-space: pre-wrap;
  }

  .doc-tab-pager {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: var(--affine-text-secondary-color);
  }

  .doc-tab-pager button {
    appearance: none;
    background: transparent;
    border: 1px solid var(--affine-border-color);
    color: var(--affine-text-primary-color);
    padding: 2px 8px;
    border-radius: 4px;
    cursor: pointer;
    font-family: inherit;
    font-size: 11px;
  }

  .doc-tab-pager button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .doc-tab-empty {
    font-size: 11.5px;
    color: var(--affine-text-secondary-color);
    padding: 8px 12px 12px;
  }
`;
